import type { Plan } from '@/entities/plan';
import type { Period } from '@/entities/types';
import { getPeriodStartAt } from '@/utils/datetime';
import {
  formatMonthDayYear,
  formatRelativeDay,
  formatWeekdayMonthDayYear,
} from '@/utils/format-datetime';
import { CheckInSection } from '../CheckInSection';
import { InsightsPanel } from '../CheckInSection/InsightsPanel';
import { PeriodPaneDisplay } from '../PeriodPaneDisplay';
import { useCheckInView } from './useCheckInView';

interface CheckInViewProps {
  plan: Plan;
  period: Period;
  targetStartAt: Date;
  targetStartAtLocal: string;
}

export function CheckInView({
  plan,
  period,
  targetStartAt,
  targetStartAtLocal,
}: CheckInViewProps) {
  const periodStartAt = getPeriodStartAt(targetStartAt, period);
  const dayTitle = formatRelativeDay(new Date(), periodStartAt);
  const isSpecialDay = ['Today', 'Yesterday', 'Tomorrow'].includes(dayTitle);
  const title = dayTitle;
  const subtitle = isSpecialDay
    ? formatWeekdayMonthDayYear(periodStartAt)
    : formatMonthDayYear(periodStartAt);

  const {
    responses,
    handleResponseChange,
    handleSubmit,
    isPending,
    error,
    insights,
    hasInsights,
  } = useCheckInView({ plan, period, targetStartAt, targetStartAtLocal });

  return (
    <PeriodPaneDisplay
      title={title}
      subtitle={subtitle}
      period={period}
      periodStartAt={targetStartAt}
      className="mx-auto flex min-h-full w-full max-w-[650px] flex-col px-5 py-7 md:px-10 md:py-10"
    >
      <div className="relative flex flex-1 flex-col">
        <CheckInSection
          hasInsights={hasInsights}
          responses={responses}
          onResponseChange={handleResponseChange}
          onSubmit={handleSubmit}
          isPending={isPending}
          error={error}
        />
        {hasInsights && (
          <div className="absolute top-0 left-full ml-8 hidden w-96 md:block">
            <InsightsPanel insights={insights} />
          </div>
        )}
      </div>
    </PeriodPaneDisplay>
  );
}
